/**
 * Lightweight i18n for Laviens.
 *
 * Italian is the default and lives at the root paths (/, /treatments, ...).
 * English mirrors the same routes under the `/en/` prefix
 * (/en/, /en/treatments, ...).
 *
 * No external library — just a typed string map plus a `useLang()` hook
 * that reads the current language from `useLocation()`. Pre-rendered HTML
 * pages know their language from the route, so server output matches the
 * client without hydration mismatches.
 */
import { useLocation } from "react-router-dom";

export type Lang = "it" | "en";

export const DEFAULT_LANG: Lang = "it";
export const SUPPORTED_LANGS: Lang[] = ["it", "en"];

/** Detect the current language from a URL pathname. */
export const langFromPath = (pathname: string): Lang =>
  pathname === "/en" || pathname.startsWith("/en/") ? "en" : "it";

/** Strip the `/en` prefix (if any) to recover the canonical Italian path. */
export const stripLangPrefix = (pathname: string): string => {
  if (pathname === "/en") return "/";
  if (pathname.startsWith("/en/")) return pathname.slice(3) || "/";
  return pathname;
};

/** Build a localized path for the given language. */
export const localizedPath = (path: string, lang: Lang): string => {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (lang === "it") return clean;
  // English: prepend /en, but preserve "/" → "/en"
  if (clean === "/") return "/en";
  return `/en${clean}`;
};

/** Hook returning current language + helpers. */
export const useLang = () => {
  const { pathname } = useLocation();
  const lang = langFromPath(pathname);
  return {
    lang,
    isEn: lang === "en",
    /** Localized path builder bound to the current language. */
    lp: (path: string) => localizedPath(path, lang),
    /** Counterpart path in the other language (for the switcher). */
    otherLangPath: stripLangPrefix(pathname) === "/"
      ? lang === "it" ? "/en" : "/"
      : localizedPath(stripLangPrefix(pathname), lang === "it" ? "en" : "it"),
    /** Translate a key with the current language. */
    t: (key: TranslationKey): string => translate(key, lang),
  };
};

// ────────────────────────────────────────────────────────────────────
// Translation dictionary
// Keep keys flat + namespaced (e.g. "nav.home", "treatments.cta")
// to keep call sites short and the lookup O(1).
// ────────────────────────────────────────────────────────────────────

const STRINGS = {
  // ── Navigation / chrome ──
  "nav.home": { it: "Home", en: "Home" },
  "nav.treatments": { it: "Trattamenti", en: "Treatments" },
  "nav.about": { it: "Chi siamo", en: "About" },
  "nav.journal": { it: "Journal", en: "Journal" },
  "nav.contact": { it: "Contatti", en: "Contact" },
  "nav.booking": { it: "Prenota", en: "Book" },
  "nav.giftcard": { it: "Gift Card", en: "Gift Card" },
  "nav.menu": { it: "Menù", en: "Menu" },
  "nav.close": { it: "Chiudi", en: "Close" },
  "nav.openMenu": { it: "Apri menù", en: "Open menu" },

  // ── Language switcher ──
  "lang.italian": { it: "Italiano", en: "Italian" },
  "lang.english": { it: "Inglese", en: "English" },
  "lang.switchToEnglish": { it: "English", en: "English" },
  "lang.switchToItalian": { it: "Italiano", en: "Italiano" },

  // ── Common CTAs ──
  "cta.book": { it: "Prenota una sessione", en: "Book a session" },
  "cta.bookShort": { it: "Prenota", en: "Book" },
  "cta.call": { it: "Chiama", en: "Call" },
  "cta.whatsapp": { it: "WhatsApp", en: "WhatsApp" },
  "cta.discover": { it: "Scopri", en: "Discover" },
  "cta.readMore": { it: "Leggi", en: "Read" },
  "cta.viewAll": { it: "Vedi tutto", en: "View all" },

  // ── Footer ──
  "footer.tagline": {
    it: "Un rituale di benessere, pensato interamente per te.",
    en: "A wellness ritual, designed entirely for you.",
  },
  "footer.studioTitle": { it: "Studio", en: "Studio" },
  "footer.exploreTitle": { it: "Esplora", en: "Explore" },
  "footer.contactTitle": { it: "Contatti", en: "Contact" },
  "footer.location": {
    it: "Sesto Fiorentino (Firenze), Italia",
    en: "Sesto Fiorentino (Florence), Italy",
  },
  "footer.hoursWeek": {
    it: "Feriali 8:00–18:00",
    en: "Weekdays 8:00 AM – 6:00 PM",
  },
  "footer.hoursWeekend": {
    it: "Festivi 9:00–20:00",
    en: "Weekends 9:00 AM – 8:00 PM",
  },
  "footer.privacy": { it: "Privacy", en: "Privacy" },
  "footer.cookies": { it: "Cookie", en: "Cookies" },
  "footer.rights": {
    it: "Laviens Studio Olistico · Tutti i diritti riservati",
    en: "Laviens Wellness Studio · All rights reserved",
  },

  // ── Hero / common labels ──
  "common.appointmentOnly": {
    it: "Solo su appuntamento",
    en: "By appointment only",
  },
  "common.studioEyebrow": {
    it: "Studio olistico — Firenze",
    en: "Wellness studio — Florence",
  },
  "common.minutes": { it: "minuti", en: "minutes" },
  "common.minutesShort": { it: "min", en: "min" },
  "common.hour": { it: "ora", en: "hour" },
  "common.hours": { it: "ore", en: "hours" },

  // ── Home ──
  "home.heroTitle": { it: "Laviens.", en: "Laviens." },
  "home.heroSubtitle": {
    it: "Un rituale di benessere, pensato interamente per te.",
    en: "A wellness ritual, designed entirely for you.",
  },
  "home.section.massageEyebrow": {
    it: "Massaggi Olistici",
    en: "Holistic Massages",
  },
  "home.section.massageTitle": {
    it: "Un ritmo nuovo, fino alla punta delle dita.",
    en: "A new rhythm, down to your fingertips.",
  },
  "home.section.massageDesc": {
    it: "Il massaggio è un modo naturale per migliorare le prestazioni quotidiane, vivere senza dolore e ritrovare il benessere generale del corpo e della mente.",
    en: "Massage is a natural way to improve daily performance, live without pain, and restore overall well-being of body and mind.",
  },
  "home.section.massageCta": {
    it: "Prenota un massaggio",
    en: "Book a massage",
  },
  "home.section.skinEyebrow": { it: "Skin Care", en: "Skin Care" },
  "home.section.skinTitle": {
    it: "Cura la tua pelle, come un gesto di ascolto.",
    en: "Care for your skin as a gesture of listening.",
  },
  "home.section.skinDesc": {
    it: "Prendersi cura della pelle non è solo una coccola: è una precauzione necessaria per mantenerla bella, sana e luminosa nel tempo.",
    en: "Caring for your skin isn't just a treat: it's a necessary practice to keep it beautiful, healthy, and radiant over time.",
  },
  "home.section.skinCta": {
    it: "Scopri i trattamenti",
    en: "Discover treatments",
  },
  "home.section.aromaEyebrow": { it: "Aromaterapia", en: "Aromatherapy" },
  "home.section.aromaTitle": {
    it: "Il respiro che riporta l'equilibrio.",
    en: "The breath that restores balance.",
  },
  "home.section.aromaDesc": {
    it: "L'aromaterapia unisce il tocco premuroso del massaggio agli oli essenziali naturali. Due sensi primordiali — il tatto e l'olfatto — che aiutano a trovare armonia fisica, mentale ed emotiva.",
    en: "Aromatherapy combines the caring touch of massage with natural essential oils. Two primal senses — touch and smell — that help find physical, mental, and emotional harmony.",
  },
  "home.section.aromaCta": {
    it: "Provare il benessere",
    en: "Try the experience",
  },
  "home.pull.manifesto1": {
    it: "Il corpo ricorda tutto.",
    en: "The body remembers everything.",
  },
  "home.pull.manifesto2": {
    it: "Noi insegniamo a lasciar andare.",
    en: "We teach how to let go.",
  },
  "home.pull.manifestoAttr": {
    it: "— Manifesto Laviens",
    en: "— Laviens Manifesto",
  },
  "home.pull.review": {
    it: "Ci mette passione e dedizione.",
    en: "She brings real passion and dedication.",
  },
  "home.pull.reviewAttr": {
    it: "— Arianna · Massaggio Ayurvedico",
    en: "— Arianna · Ayurvedic Massage",
  },
  "home.discoverEyebrow": { it: "Leggi o esplora", en: "Read or explore" },
  "home.discoverTitle1": { it: "Approfondisci,", en: "Go deeper," },
  "home.discoverTitle2": { it: "senza fretta", en: "without rushing" },
  "home.locationsTitle": {
    it: "Massaggi nella tua zona",
    en: "Massages in your area",
  },
  "home.locationFirenze": {
    it: "Massaggi Firenze",
    en: "Massages in Florence",
  },
  "home.locationSesto": {
    it: "Massaggi Sesto Fiorentino",
    en: "Massages in Sesto Fiorentino",
  },
  "home.locationToscana": {
    it: "Massaggi in Toscana",
    en: "Massages in Tuscany",
  },
  "home.guideEyebrow": { it: "Guida", en: "Guide" },
  "home.guideLinkSvedese": {
    it: "Svedese o decontratturante? Come scegliere",
    en: "Swedish or deep-tissue? How to choose",
  },
  "home.guideTeaserSvedese": {
    it: "Due massaggi diversi — e una guida pratica per capire quale prenotare in base a come stai.",
    en: "Two different massages — and a practical guide to help you choose based on how you feel.",
  },
  "home.guideLinkLinfo": {
    it: "Cos'è il linfodrenaggio e quando serve",
    en: "What lymphatic drainage is — and when it helps",
  },
  "home.guideTeaserLinfo": {
    it: "Non è un massaggio rilassante e non è un anticellulite. Il metodo Vodder spiegato bene.",
    en: "It's not a relaxing massage and it's not anti-cellulite. The Vodder method, explained clearly.",
  },
  "home.locationsAccentEyebrow": { it: "Dove siamo", en: "Where to find us" },
  "home.allJournal": { it: "Tutto il Journal", en: "Read the Journal" },
  "home.closingPull1": {
    it: "Quando vuoi tornare a respirare,",
    en: "When you're ready to breathe again,",
  },
  "home.closingPull2": { it: "ci trovi qui.", en: "you'll find us here." },
  "home.closingPullAttr": {
    it: "— Laviens, Sesto Fiorentino",
    en: "— Laviens, Sesto Fiorentino",
  },

  // ── Treatments page ──
  "treatments.heroEyebrow": { it: "Trattamenti", en: "Treatments" },
  "treatments.heroTitle": { it: "Un catalogo,", en: "A catalogue" },
  "treatments.heroSubtitle": {
    it: "di piccoli rituali per ritrovare il proprio ritmo.",
    en: "of small rituals to rediscover your own rhythm.",
  },
  "treatments.tab.massages": { it: "Massaggi", en: "Massages" },
  "treatments.tab.rituals": { it: "Rituali", en: "Rituals" },
  "treatments.tab.waxing": { it: "Depilazione", en: "Waxing" },
  "treatments.tab.skincare": { it: "Skin Care", en: "Skin Care" },
  "treatments.ctaLeadPart1": { it: "Scegli il", en: "Pick the" },
  "treatments.ctaLeadEm": { it: "rituale giusto", en: "right ritual" },
  "treatments.ctaLeadPart2": {
    it: "— basta una telefonata.",
    en: "— a phone call is all it takes.",
  },
  "treatments.detailEyebrow": { it: "Approfondisci", en: "Learn more" },
  "treatments.detailTitle1": {
    it: "Una pagina dedicata,",
    en: "A dedicated page,",
  },
  "treatments.detailTitleEm": {
    it: "per ogni trattamento",
    en: "for every treatment",
  },
  "treatments.detailLead": {
    it: "Per capire come si svolge una seduta, quanto dura, a chi è adatto e quando conviene sceglierlo, abbiamo scritto una pagina dedicata per ogni trattamento principale.",
    en: "We've written a dedicated page for each main treatment — how a session unfolds, how long it lasts, who it's for, and when to choose it.",
  },

  // ── Booking page ──
  "booking.heroEyebrow": { it: "Prenota", en: "Book" },
  "booking.heroTitle": {
    it: "Un tempo solo per te.",
    en: "A moment just for you.",
  },
  "booking.heroSubtitle": {
    it: "Basta una telefonata.",
    en: "A phone call is all it takes.",
  },
  "booking.intro": {
    it: "Laviens accoglie una persona alla volta, su appuntamento. Per scegliere insieme il rituale più adatto a come stai, parliamo qualche minuto al telefono o su WhatsApp.",
    en: "Laviens welcomes one guest at a time, by appointment. We take a few minutes on the phone or WhatsApp to choose the ritual best suited to how you feel.",
  },
  "booking.whyPhoneTitle": {
    it: "Perché solo telefono",
    en: "Why phone-only",
  },
  "booking.whyPhoneBody": {
    it: "Perché ascoltare prima è parte del trattamento: non scegliamo un massaggio in base a un menù, ma in base a come arrivi quel giorno.",
    en: "Because listening first is part of the treatment: we don't pick a massage from a menu — we pick one based on how you arrive that day.",
  },
  "booking.hoursTitle": { it: "Orari", en: "Hours" },
  "booking.weekdays": {
    it: "Feriali · 8:00 – 18:00",
    en: "Weekdays · 8:00 AM – 6:00 PM",
  },
  "booking.weekends": {
    it: "Festivi · 9:00 – 20:00",
    en: "Weekends · 9:00 AM – 8:00 PM",
  },
  "booking.callNow": {
    it: "Chiama ora · +39 349 246 8186",
    en: "Call now · +39 349 246 8186",
  },
  "booking.whatsappNow": {
    it: "Scrivi su WhatsApp",
    en: "Message on WhatsApp",
  },

  // ── Contact page ──
  "contact.heroEyebrow": { it: "Contatti", en: "Contact" },
  "contact.heroTitle": { it: "Parliamone.", en: "Let's talk." },
  "contact.heroSubtitle": {
    it: "Rispondiamo di persona — basta una telefonata o un messaggio.",
    en: "We answer in person — a call or message is all it takes.",
  },
  "contact.phoneLabel": { it: "Telefono", en: "Phone" },
  "contact.whatsappLabel": { it: "WhatsApp", en: "WhatsApp" },
  "contact.emailLabel": { it: "Email", en: "Email" },
  "contact.addressLabel": { it: "Dove siamo", en: "Where to find us" },
  "contact.hoursLabel": { it: "Orari", en: "Hours" },
  "contact.directionsCta": {
    it: "Apri in Google Maps",
    en: "Open in Google Maps",
  },

  // ── About page ──
  "about.heroEyebrow": { it: "Chi siamo", en: "About" },
  "about.heroTitle": { it: "Laviens — la storia.", en: "Laviens — the story." },
  "about.heroSubtitle": {
    it: "Uno studio olistico privato a Sesto Fiorentino, con Regina.",
    en: "A private wellness studio in Sesto Fiorentino, with Regina.",
  },

  // ── FAQ section ──
  "faq.title": { it: "Domande frequenti", en: "Frequently asked questions" },

  // ── Trust strip ──
  "trust.appointmentOnly": {
    it: "Solo su appuntamento",
    en: "By appointment only",
  },
  "trust.privateStudio": {
    it: "Studio privato a Sesto Fiorentino",
    en: "Private studio in Sesto Fiorentino",
  },
  "trust.regina": {
    it: "Regina · 15+ anni di pratica",
    en: "Regina · 15+ years of practice",
  },
  "trust.reviews": {
    it: "Recensioni 5★",
    en: "5-star reviews",
  },

  // ── Testimonials ──
  "reviews.eyebrow": {
    it: "Recensioni · verificate su Treatwell e Google",
    en: "Reviews · verified on Treatwell and Google",
  },
  "reviews.title1": { it: "Cosa dicono di", en: "What they say about" },
  "reviews.titleEm": { it: "Regina.", en: "Regina." },
  "reviews.countLabel": {
    it: "recensioni verificate",
    en: "verified reviews",
  },
  "reviews.moreCta": {
    it: "Leggi tutte le recensioni su Treatwell →",
    en: "Read all reviews on Treatwell →",
  },

  // ── Cookie banner ──
  "cookie.title": {
    it: "Rispettiamo la tua privacy",
    en: "We respect your privacy",
  },
  "cookie.body": {
    it: "Usiamo cookie tecnici e, con il tuo consenso, cookie di misurazione anonimi per migliorare il sito. Puoi accettare, rifiutare o personalizzare in qualsiasi momento.",
    en: "We use technical cookies and, with your consent, anonymous measurement cookies to improve the site. You can accept, decline, or customize at any time.",
  },
  "cookie.accept": { it: "Accetta tutti", en: "Accept all" },
  "cookie.decline": { it: "Solo necessari", en: "Necessary only" },
  "cookie.policy": { it: "Cookie policy", en: "Cookie policy" },
} as const;

export type TranslationKey = keyof typeof STRINGS;

export const translate = (key: TranslationKey, lang: Lang): string => {
  const entry = STRINGS[key];
  if (!entry) return key;
  return entry[lang] ?? entry[DEFAULT_LANG];
};
